import React, { ReactNode, useContext, useMemo } from 'react';

import { AbstractContextType } from './AbstractProvider';
import {
  DeliveryContext,
  InquiryContext,
  LocationContext,
  OrderPositionContext,
  PackageUnitContext,
  ResourceContext,
  SectionContext,
  StatisticContext,
  UserContext,
  VehicleContext,
  VehicleTypeContext,
} from './ContextWrapper';
import ILocation from '../models/ILocation';
import IVehicle from '../models/IVehicle';
import ISection from '../models/ISection';
import IUser from '../models/IUser';
import IResource from '../models/IResource';
import IInquiry from '../models/IInquiry';
import IOrderPosition from '../models/IOrder';
import IVehicleType from '../models/IVehicleType';
import IStatistic from '../models/IStatistic';
import IPackageUnit from '../models/IPackageUnit';
import IDelivery from '../models/IDelivery';

import useWebSocket from 'react-use-websocket';
import logger from '../utility/logger';

export function WebSocketListener(props: { children: ReactNode }) {
  const contextMap = useMemo(
    () => new Map<string, AbstractContextType<any>>(),
    [],
  );

  contextMap.set(
    'resource',
    useContext(ResourceContext) as AbstractContextType<IResource>,
  );
  contextMap.set(
    'location',
    useContext(LocationContext) as AbstractContextType<ILocation>,
  );
  contextMap.set(
    'vehicle',
    useContext(VehicleContext) as AbstractContextType<IVehicle>,
  );
  contextMap.set(
    'vehicle_type',
    useContext(VehicleTypeContext) as AbstractContextType<IVehicleType>,
  );
  contextMap.set('user', useContext(UserContext) as AbstractContextType<IUser>);
  contextMap.set(
    'section',
    useContext(SectionContext) as AbstractContextType<ISection>,
  );
  contextMap.set(
    'inquiry',
    useContext(InquiryContext) as AbstractContextType<IInquiry>,
  );
  contextMap.set(
    'order-position',
    useContext(OrderPositionContext) as AbstractContextType<IOrderPosition>,
  );
  contextMap.set(
    'statistics',
    useContext(StatisticContext) as AbstractContextType<IStatistic>,
  );
  contextMap.set(
    'package-unit',
    useContext(PackageUnitContext) as AbstractContextType<IPackageUnit>,
  );
  contextMap.set(
    'delivery',
    useContext(DeliveryContext) as AbstractContextType<IDelivery>,
  );

  const { readyState } = useWebSocket(process.env.REACT_APP_WEBSOCKET_URL, {
    onOpen: () => logger.websocket.info('Connection opened'),
    onClose: () => logger.websocket.warn('Connection closed'),
    onError: (event: any) => logger.websocket.error('Connection error', event),
    onMessage: (event: any) => {
      try {
        const msg = JSON.parse(event.data);

        if (msg.type === 'ARCHIVE') {
          logger.websocket.warn(
            'ARCHIVE event received - reloading page in 1.5s',
          );
          // TODO: check if refreshing all the data could be integrated in the contexts components.
          setTimeout(() => {
            window.location.reload();
          }, 1500);
          return;
        }

        // Parse Date attributes from string format
        parseDateAttributes(msg);

        const ctx = contextMap.get(msg.path);
        if (!ctx) {
          logger.websocket.error(
            `No context available for websocket path '${msg.path}'`,
          );
          return;
        }

        // Handle different message types
        if (msg.type === 'CREATE') {
          logger.websocket.debug(`CREATE event for ${msg.path}`, msg.payload);
          ctx.add(msg.payload);
        } else if (msg.type === 'UPDATE') {
          logger.websocket.debug(`UPDATE event for ${msg.path}`, msg.payload);
          ctx.update(msg.payload);
        } else if (msg.type === 'DELETE') {
          logger.websocket.debug(
            `DELETE event for ${msg.path}`,
            msg.payload.id,
          );
          ctx.remove(msg.payload.id);
        }
      } catch (error) {
        logger.websocket.error('Error processing WebSocket message', error);
      }
    },
    // Reconnect-Strategie mit Exponential Backoff
    shouldReconnect: () => true,
    reconnectAttempts: 10, // Max 10 Versuche
    reconnectInterval: (attemptNumber) =>
      Math.min(1000 * 2 ** attemptNumber, 30000), // Exponential: 1s, 2s, 4s, 8s, 16s, 30s max
    // Heartbeat-Konfiguration (< Backend 30s)
    heartbeat: {
      message: 'ping',
      returnMessage: 'pong',
      timeout: 25000, // 25s (weniger als Backend 30s)
      interval: 20000, // Alle 20s prüfen
    },
    // Share-Instanz für Performance
    share: false,
    // Filter Nachrichten
    filter: () => true,
    // Retry on Error
    retryOnError: true,
  });

  // expected datetime format "2022-10-13T14:44:24.004Z"
  function parseDateAttributes(o: any) {
    for (const a in o) {
      if (
        typeof o[a] === 'string' &&
        /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z/.test(o[a])
      ) {
        o[a] = new Date(o[a]);
      } else if (typeof o[a] === 'object') {
        parseDateAttributes(o[a]);
      }
    }
  }

  return <> {props.children} </>;
}
